import { useEffect, useRef } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'

import FieldWrapper from '../FieldWrapper'
import ViewFileHistoryModalButton from '@/components/Elements/Button/ViewFileHistoryModalButton'

import { allFileTypes } from '@/utils/constants'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { useDispatch } from 'react-redux'
import useFile from '@/features/files/useFile'
import { Button, Flex, VStack } from '@chakra-ui/react'
import { MdDeleteOutline } from 'react-icons/md'

const FileInput = ({
  name,
  label,
  isRow = false,
  disabled = false,
  fileType,
  isMultiple = false,
  canRemove = false,
  canUploadContinuously = false,
  ...rest
}) => {
  const { control, getValues, setValue, register } = useFormContext()

  const dispatch = useDispatch()
  const { useRemoveFile, useUploadFile } = useFile()
  const uploadFile = useUploadFile()

  const fileDetails = useWatch({ name: name, control }) ?? []
  const showUploadInput = !disabled && (canUploadContinuously || fileDetails.length === 0)

  useEffect(() => {
    register(name)
  }, [name, register])

  const inputRef = useRef(null)

  const handleFileUpload = async (e) => {
    if (!fileType) {
      console.error('File type is required')
      return
    }

    if (e.target.files.length === 0) {
      console.error('No file selected')
      return
    }

    try {
      dispatch(showLoadingOverlay())

      const res = await uploadFile.mutateAsync({ version: 0, type: fileType, files: e.target.files })
      if (Array.isArray(res?.data?.data) && res.data.data.length > 0) {
        if (canUploadContinuously) {
          setValue(name, [...(getValues(name) ?? []), ...res.data.data], { shouldDirty: true })
        } else {
          setValue(name, res.data.data, { shouldDirty: true })
        }
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoadingOverlay())
      // Clear the file input so the same file can be selected again
      try {
        if (inputRef.current) inputRef.current.value = ''
      } catch (err) {
        // ignore
      }
    }
  }

  const handleRemoveFile = () => {
    setValue(name, null)
  }

  return (
    <FieldWrapper name={name} label={label} isRow={isRow} {...rest}>
      <VStack alignItems={'flex-start'}>
        <Flex>
          {fileDetails.length > 0 && (
            <ViewFileHistoryModalButton fileDetails={fileDetails} showFileName={false} canRemove={canRemove} />
          )}
          {fileDetails.length > 0 && canRemove && !disabled && (
            <Button variant={'ghost'} onClick={handleRemoveFile}>
              <MdDeleteOutline />
            </Button>
          )}
        </Flex>
        {showUploadInput && (
          <input ref={inputRef} type="file" accept={allFileTypes} onChange={handleFileUpload} multiple={isMultiple} />
        )}
      </VStack>
    </FieldWrapper>
  )
}

export default FileInput
